// The Director's stage as pure state (spec-v4 section 8.4): what's on it, which item has the focus, the history that
// `back` walks, and whether the user minimised it to the call banner. No DOM, no store: src/stage/actions.ts feeds it
// events (the call starting and ending, the `stage` directive, taps) and stores the result. Unit-tested in
// state.test.ts.
//
//   call starts      -> open, showing what's on screen (the project or collection), or nothing yet (Home)
//   `stage` replace  -> swaps the items; push -> the current items go into the history first
//   `back`           -> the previous items (nothing to go back to: no change)
//   minimise (▾)     -> the banner; "Back to stage" restores it
//   a `stage` directive while minimised brings the stage back, unless the user is busy (typing, or a sheet with
//   unsaved edits): then it stays minimised and the banner pulses "Your Director is showing you …"
//   call ends        -> closed and cleared
import type { StageDirective, StageItem } from "../types";

export type StageFrame = { items: StageItem[]; focus: number; title: string | null };

export type StageState = {
  /** a call is on and the stage has something to show */
  open: boolean;
  items: StageItem[];
  /** the item in focus (multi-item stages; the audition's "now" candidate) */
  focus: number;
  /** the directive's title ("Voices for Milo"), else the focused item's label */
  title: string | null;
  /** earlier stages, newest last (push); `back` pops */
  history: StageFrame[];
  /** the user tucked it into the call banner (▾) */
  minimised: boolean;
  /** the banner's pulse when the Director showed something while the user was busy */
  attention: string | null;
  /** the letter the user picked on a show_choices stage */
  picked: string | null;
  /** audition candidates heard on this stage (voice ids, or urls), on top of the directive's own `heard` */
  heard: string[];
};

/** `back` goes this far, then the oldest stages drop off. */
export const HISTORY_MAX = 20;
/** show_choices lays out at most this many (A-D). */
export const MAX_CHOICES = 4;

export const closedStage = (): StageState => ({ open: false, items: [], focus: 0, title: null, history: [], minimised: false, attention: null, picked: null, heard: [] });

export type StageEvent =
  /** the call connected; `items` is what's on screen (empty on Home: the stage waits for the Director) */
  | { type: "call_start"; items: StageItem[] }
  | { type: "call_end" }
  /** a `stage` directive; `busy`: the user is typing or has a sheet with unsaved edits open (the bring-back guard) */
  | { type: "directive"; d: StageDirective; busy: boolean }
  | { type: "back" }
  | { type: "minimise" }
  | { type: "restore" }
  | { type: "focus"; index: number }
  | { type: "heard"; id: string }
  | { type: "pick"; letter: string };

/**
 * How far `focus` goes: the items, or, when the stage is one audition, its candidates (the "now" voice; spec 8.3).
 */
export const focusLimit = (items: StageItem[]) => (items.length === 1 && items[0]!.kind === "audition" ? items[0]!.candidates.length : items.length);
/** The item in focus (an audition's focus is its candidate, so the item is the audition itself). */
export const focusedItem = (s: Pick<StageState, "items" | "focus">): StageItem | undefined => s.items[Math.min(s.focus, s.items.length - 1)];

const clamp = (i: number | undefined, n: number) => (n <= 0 ? 0 : Math.min(n - 1, Math.max(0, Math.floor(Number.isFinite(i) ? (i as number) : 0))));

export function reduceStage(s: StageState, e: StageEvent): StageState {
  switch (e.type) {
    case "call_start":
      return { ...closedStage(), open: e.items.length > 0, items: e.items };
    case "call_end":
      return !s.open && !s.items.length && !s.history.length ? s : closedStage();
    case "directive": {
      const items = e.d.items;
      if (!items.length) return s;
      const visible = s.open && !s.minimised;
      const pushed = e.d.mode === "push" && s.items.length ? [...s.history, frameOf(s)].slice(-HISTORY_MAX) : s.history;
      const next: StageState = { ...s, open: true, items, focus: clamp(e.d.focus, focusLimit(items)), title: e.d.title?.trim() || null, history: pushed, picked: null, heard: [] };
      if (visible) return { ...next, minimised: false, attention: null };
      // bring the user back, unless they're in the middle of something
      if (e.busy) return { ...next, minimised: true, attention: `Your Director is ${stageDoing(next, true)}` };
      return { ...next, minimised: false, attention: null };
    }
    case "back": {
      const prev = s.history.at(-1);
      if (!prev) return s;
      return { ...s, items: prev.items, focus: prev.focus, title: prev.title, history: s.history.slice(0, -1), picked: null, heard: [], minimised: false, attention: null };
    }
    case "minimise":
      return s.open && !s.minimised ? { ...s, minimised: true } : s;
    case "restore":
      return s.open ? { ...s, minimised: false, attention: null } : s;
    case "focus": {
      const focus = clamp(e.index, focusLimit(s.items));
      return focus === s.focus ? s : { ...s, focus };
    }
    case "heard":
      return !e.id || s.heard.includes(e.id) ? s : { ...s, heard: [...s.heard, e.id] };
    case "pick":
      return { ...s, picked: e.letter };
  }
}

const frameOf = (s: StageState): StageFrame => ({ items: s.items, focus: s.focus, title: s.title });

// ------------------------------------------------------------------ labels

const KIND_LABEL: Record<StageItem["kind"], string> = {
  project: "the video",
  collection: "the collection",
  character: "the character",
  sheet: "the character sheet",
  frame: "the frame",
  clip: "the clip",
  picture: "the picture",
  video: "the video",
  script: "the script",
  estimate: "the price",
  library: "your library",
  models: "the models",
  formats: "the video types",
  choices: "a choice",
  audition: "some voices",
  references: "the reference pictures",
  suno: "the Suno pack",
  showcase: "the models side by side",
};

/** What one item is called in the banner and the stage head ("Pip's sheet", "Voices for Milo", "the script"). */
export function itemLabel(it: StageItem | undefined): string {
  if (!it) return "something";
  if (it.title?.trim()) return it.title.trim();
  if (it.kind === "choices") return it.question?.trim() || KIND_LABEL.choices;
  if (it.kind === "audition") return it.for ? `voices for ${it.for}` : KIND_LABEL.audition;
  return KIND_LABEL[it.kind];
}

/** The stage's title: the directive's, else the focused item's label. */
export const stageTitle = (s: Pick<StageState, "title" | "items" | "focus">) => s.title ?? itemLabel(focusedItem(s));

/** What the Director is doing on stage, for the banner: "showing (you) Pip's sheet", or "asking (you): Which Pip?". */
export function stageDoing(s: Pick<StageState, "title" | "items" | "focus">, you = false): string {
  const it = focusedItem(s);
  if (it?.kind === "choices" && !s.title && it.question?.trim()) return `asking${you ? " you" : ""}: ${it.question.trim()}`;
  return `showing${you ? " you" : ""} ${stageTitle(s)}`;
}

/** The ids of an item, for the tool call context ({kind, ids}, section 8.4) and for "is this about the open video?". */
export function itemIds(it: StageItem): Record<string, string | number> {
  switch (it.kind) {
    case "project":
    case "script":
    case "estimate":
      return { projectId: it.projectId };
    case "frame":
    case "clip":
    case "references":
      return { projectId: it.projectId, sceneId: it.sceneId };
    case "suno":
      return { projectId: it.projectId };
    case "video":
      return { projectId: it.projectId, ...(it.version ? { version: it.version } : {}), ...(it.renderId ? { renderId: it.renderId } : {}) };
    case "collection":
      return { collectionId: it.collectionId };
    case "character":
      return { characterId: it.characterId };
    case "sheet":
      return { characterId: it.characterId, versions: it.versions.join(",") };
    case "library":
      return { scope: it.scope };
    case "picture":
      return { assetId: it.assetId, ...(it.projectId ? { projectId: it.projectId } : {}) };
    case "models":
      return it.step ? { step: it.step } : {};
    case "formats":
      return {};
    case "choices":
      return { choiceKind: it.choiceKind, items: it.items.map((c) => `${c.letter}:${c.id}`).join(",") };
    case "audition":
      // V4-D5: the candidates by letter (sample ids, else voice ids), so play_and_wait can play "choice B"
      return { for: it.for, items: it.candidates.map((a, i) => `${"ABCD"[i] ?? i + 1}:${a.assetId ?? a.voiceId ?? ""}`).join(",") };
    case "showcase":
      return { style: it.style, models: it.models.join(","), ...(it.step === "frame" ? { step: "frame" } : {}) };
  }
}

/** What the Director's tool calls carry about the stage (null when it's closed): `{kind, ids}` of the focused item. */
export function stageContext(s: StageState): { kind: StageItem["kind"]; ids: Record<string, string | number>; minimised: boolean } | null {
  const it = s.open ? focusedItem(s) : undefined;
  return it ? { kind: it.kind, ids: itemIds(it), minimised: s.minimised } : null;
}

// ------------------------------------------------------------------ where the stage shows

/** Items that live inside a video's editing suite (its preview already is the stage for them on desktop, Q11). */
// (references and suno aren't: the suite shows them only inside folds, so the stage draws them even on the video)
const SUITE_KINDS = new Set<StageItem["kind"]>(["project", "frame", "clip", "video", "script", "estimate"]);
export const aboutProject = (it: StageItem, projectId: string) => SUITE_KINDS.has(it.kind) && "projectId" in it && it.projectId === projectId;

/**
 * Where the stage draws:
 *   none     no call, or nothing to show yet
 *   banner   minimised: the call banner with "Back to stage"
 *   suite    desktop, on the video the stage is about: the suite's preview is the stage, no overlay (Q11)
 *   overlay  the stage itself (phones: full screen; desktop: the main pane, conversation on the right)
 */
export type StageSurface = "none" | "banner" | "suite" | "overlay";
export function stageSurface(s: StageState, ctx: { desktop: boolean; suiteProject: string | null }): StageSurface {
  if (!s.open || !s.items.length) return "none";
  if (s.minimised) return "banner";
  if (ctx.desktop && ctx.suiteProject && s.items.every((it) => aboutProject(it, ctx.suiteProject!))) return "suite";
  return "overlay";
}

/** What's on screen when a call connects (the stage starts with it): a video, a collection or a character. */
export function screenItems(path: string): StageItem[] {
  // v4 (T4): the review screen is its own player; the stage waits for the Director there (it would cover the player)
  if (/^\/p\/[^/?#]+\/review(?:[?#]|$)/.test(path)) return [];
  const m = /^\/(p|k)\/([^/?#]+)(?:\/cast\/([^/?#]+))?/.exec(path);
  if (!m) return [];
  const [, level, id, character] = m as unknown as [string, "p" | "k", string, string | undefined];
  if (character) return [{ kind: "character", characterId: decodeURIComponent(character) }];
  return level === "p" ? [{ kind: "project", projectId: decodeURIComponent(id) }] : [{ kind: "collection", collectionId: decodeURIComponent(id) }];
}

// ------------------------------------------------------------------ the bring-back guard

/** The bits of the focused element the guard looks at (a plain object, so it's testable without a DOM). */
export type FocusInfo = { tag: string; type?: string | null; editable?: boolean; readOnly?: boolean; disabled?: boolean } | null;

const TEXT_INPUTS = new Set(["", "text", "search", "email", "url", "tel", "password", "number"]);
/** Is the user typing? A text field, a text area or anything contentEditable with the focus. */
export function isTyping(f: FocusInfo): boolean {
  if (!f || f.disabled || f.readOnly) return false;
  if (f.editable) return true;
  const tag = f.tag.toLowerCase();
  if (tag === "textarea") return true;
  return tag === "input" && TEXT_INPUTS.has((f.type ?? "").toLowerCase());
}

/** The guard: don't pull the user away while they're typing or have a sheet with unsaved edits open. */
export const isBusy = (g: { focus: FocusInfo; unsavedSheet: boolean }) => g.unsavedSheet || isTyping(g.focus);
